import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { ArrowLeft, ArrowRight, Check, Flag, Link2 } from "lucide-react";
import { LIKERT, CLARIFIERS, LIKERT_OPTIONS, STYLES, MULLIGAN_TIPS, computeResult } from "@/data/quiz";
import { API, trackEvent, formatApiError } from "@/lib/api";

const EASE = [0.22, 1, 0.36, 1];
const TOTAL = 30;

export default function Quiz() {
  const [step, setStep] = useState(0); // 0-27 likert, 28-29 clarifiers, 30 = gate
  const [answers, setAnswers] = useState(Array(TOTAL).fill(null));
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [resultId, setResultId] = useState(null);
  const [copied, setCopied] = useState(false);

  const isGate = step === TOTAL;
  const isClarifier = step >= 28 && step < TOTAL;
  const result = useMemo(() => (done ? computeResult(answers) : null), [done, answers]);
  const progress = Math.min(100, Math.round(((step + (done ? 1 : 0)) / (TOTAL + 1)) * 100));

  useEffect(() => {
    document.title = "The Wired2Win Assessment — Wired 2 Win Golf";
    trackEvent("quiz_view");
  }, []);

  const answer = (val) => {
    const next = [...answers];
    next[step] = val;
    setAnswers(next);
    if (step === 0) trackEvent("quiz_start");
    setTimeout(() => setStep((s) => Math.min(TOTAL, s + 1)), 220);
  };

  const submitGate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    const r = computeResult(answers);
    try {
      const { data } = await axios.post(`${API}/quiz/results`, {
        email,
        name,
        scores: r.scores,
        archetype: r.primary,
        accent: r.accent,
        blend: r.blend,
        adaptive: r.adaptive,
        growth_zone: r.mulligan,
      });
      setResultId(data.id);
      trackEvent("lead_captured", { source: "quiz" });
      trackEvent("quiz_complete", { primary: STYLES[r.primary].name, accent: STYLES[r.accent].name });
      setDone(true);
      window.scrollTo({ top: 0 });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const copyShareLink = async () => {
    const url = `${window.location.origin}/result/${resultId}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    trackEvent("result_shared");
  };

  const question = step < 28 ? LIKERT[step].q : step < TOTAL ? CLARIFIERS[step - 28].q : "";

  return (
    <main data-testid="quiz-page" className="min-h-[100svh] flex flex-col bg-obsidian">
      <div className="fixed top-16 md:top-20 inset-x-0 z-40 h-[3px] bg-white/10">
        <motion.div
          data-testid="quiz-progress-bar"
          className="h-full bg-volt origin-left"
          animate={{ scaleX: progress / 100 }}
          initial={false}
          transition={{ duration: 0.4, ease: EASE }}
          style={{ width: "100%" }}
        />
      </div>

      <div className="flex-1 flex items-center justify-center px-6 pt-32 pb-16">
        <AnimatePresence mode="wait">
          {done ? (
            <motion.div
              key="result"
              data-testid="quiz-result"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE }}
              className="w-full max-w-3xl"
            >
              <p className="text-xs tracking-[0.25em] uppercase font-bold text-volt mb-4">Your Wired2Win Profile</p>
              <h1 className="font-display font-black tracking-tighter leading-[0.95] text-5xl sm:text-7xl uppercase">
                {STYLES[result.primary].name}
              </h1>
              <p className="mt-3 text-white/60 text-lg">{STYLES[result.primary].tagline}</p>
              <p data-testid="quiz-accent-line" className="mt-2 text-white/50 text-sm">
                Accent: <span className="text-white font-semibold">{STYLES[result.accent].name}</span>
                {result.blend && <span className="text-volt"> — a strong blend of the two</span>}
                {result.adaptive && <span className="text-volt"> — your profile is situational/adaptive (a guided read is recommended)</span>}
              </p>

              <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-6">
                {Object.entries(STYLES).map(([key, style]) => (
                  <div key={key} data-testid={`score-${key}`}>
                    <div className="flex justify-between text-xs font-bold uppercase tracking-widest mb-2">
                      <span className="text-white/70">{style.name}</span>
                      <span className="text-volt">{result.scores[key]}</span>
                    </div>
                    <div className="h-1.5 bg-white/10">
                      <motion.div
                        className="h-full bg-volt"
                        initial={{ width: 0 }}
                        animate={{ width: `${result.scores[key]}%` }}
                        transition={{ duration: 1, delay: 0.3, ease: EASE }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="border border-white/10 bg-ink p-8">
                  <p className="text-xs tracking-[0.25em] uppercase font-bold text-white/40 mb-3">How you're wired</p>
                  <p className="text-white/70 text-sm leading-relaxed">{STYLES[result.primary].strength}</p>
                  <p className="text-white/50 text-sm leading-relaxed mt-4">{STYLES[result.primary].shadow}</p>
                </div>
                <div className="border border-volt/40 bg-volt/5 p-8">
                  <p className="text-xs tracking-[0.25em] uppercase font-bold text-volt mb-3">
                    Your Mental Mulligan — borrow from {STYLES[result.mulligan].name}
                  </p>
                  <p className="text-white/80 text-sm leading-relaxed">{MULLIGAN_TIPS[result.primary]}</p>
                </div>
              </div>

              <div className="mt-12 border border-white/10 bg-ink p-8 flex flex-col sm:flex-row sm:items-center gap-6 justify-between">
                <div>
                  <p className="font-display font-bold text-2xl tracking-tight flex items-center gap-2">
                    <Check className="w-5 h-5 text-volt" /> You're wired in.
                  </p>
                  <p className="text-white/50 text-sm mt-2 max-w-md">
                    Your full profile is headed to <span className="text-white">{email}</span> — plus one mindset drill every week. Unsubscribe anytime.
                  </p>
                  {resultId && (
                    <button
                      onClick={copyShareLink}
                      data-testid="quiz-share-button"
                      className="mt-4 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-volt hover:text-white transition-colors"
                    >
                      <Link2 className="w-4 h-4" /> {copied ? "Link copied — share it with your coach" : "Copy my shareable result link"}
                    </button>
                  )}
                </div>
                <Link
                  to="/resources"
                  data-testid="quiz-result-resources-cta"
                  onClick={() => trackEvent("cta_click", { location: "quiz-result" })}
                  className="group inline-flex items-center gap-2 bg-volt text-obsidian font-display font-bold px-6 py-3.5 hover:bg-white transition-colors shrink-0"
                >
                  Start Training <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </motion.div>
          ) : isGate ? (
            <motion.div
              key="gate"
              data-testid="quiz-email-gate"
              initial={{ opacity: 0, x: 60 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -60 }}
              transition={{ duration: 0.5, ease: EASE }}
              className="w-full max-w-xl border border-white/10 bg-ink/80 backdrop-blur-xl p-8 sm:p-12"
            >
              <Flag className="w-8 h-8 text-volt mb-6" />
              <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl uppercase leading-[0.95]">
                Your profile is <span className="text-volt">ready.</span>
              </h2>
              <p className="mt-5 text-white/60 leading-relaxed">
                Enter your email to reveal your Wired2Win profile and get one mindset drill in your inbox each week. No spam, ever.
              </p>
              <form onSubmit={submitGate} className="mt-8 space-y-4" data-testid="quiz-gate-form">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="First name (optional)"
                  data-testid="quiz-gate-name"
                  className="w-full bg-white/5 border border-white/15 px-4 py-3.5 text-sm placeholder:text-white/30 focus:outline-none focus:border-volt transition-colors"
                />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com"
                  data-testid="quiz-gate-email"
                  className="w-full bg-white/5 border border-white/15 px-4 py-3.5 text-sm placeholder:text-white/30 focus:outline-none focus:border-volt transition-colors"
                />
                {error && <p data-testid="quiz-gate-error" className="text-red-400 text-xs">{error}</p>}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  disabled={saving}
                  data-testid="quiz-gate-submit"
                  className="w-full bg-volt text-obsidian font-display font-bold px-6 py-4 hover:bg-white transition-colors disabled:opacity-50"
                >
                  {saving ? "Scoring your round..." : "Reveal My Profile"}
                </motion.button>
              </form>
              <p className="mt-4 text-[11px] text-white/30 leading-relaxed">
                By continuing you agree to our <Link to="/privacy" className="underline hover:text-white/60">Privacy Policy</Link>.
              </p>
            </motion.div>
          ) : (
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 60 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -60 }}
              transition={{ duration: 0.4, ease: EASE }}
              className="w-full max-w-2xl"
            >
              <div className="flex items-baseline justify-between mb-8">
                <span data-testid="quiz-counter" className="font-display font-black text-5xl sm:text-6xl text-white/10 tracking-tighter">
                  {String(step + 1).padStart(2, "0")}
                </span>
                <span className="text-xs font-bold tracking-[0.25em] uppercase text-white/40">of 30</span>
              </div>
              {isClarifier && (
                <p className="text-xs tracking-[0.25em] uppercase font-bold text-volt mb-4">Two quick clarifiers</p>
              )}
              <h2 data-testid="quiz-question" className="font-display font-bold tracking-tight text-3xl sm:text-4xl leading-tight min-h-[7rem]">
                {question}
              </h2>

              {isClarifier ? (
                <div className="mt-10 space-y-3" role="radiogroup" aria-label="Answer options">
                  {CLARIFIERS[step - 28].options.map((o) => (
                    <motion.button
                      key={o.key}
                      whileHover={{ x: 4 }}
                      whileTap={{ scale: 0.99 }}
                      onClick={() => answer(o.key)}
                      data-testid={`quiz-clarifier-${o.key}`}
                      className={`w-full text-left border px-5 py-4 transition-colors duration-200 flex items-center gap-4 ${
                        answers[step] === o.key
                          ? "bg-volt text-obsidian border-volt"
                          : "border-white/15 text-white/70 hover:border-volt hover:text-white"
                      }`}
                    >
                      <span className={`font-display font-black text-lg ${answers[step] === o.key ? "text-obsidian/60" : "text-volt"}`}>
                        {o.key.toUpperCase()}
                      </span>
                      <span className="font-semibold text-sm sm:text-base">{o.label}</span>
                    </motion.button>
                  ))}
                </div>
              ) : (
                <div className="mt-10 grid grid-cols-2 sm:grid-cols-5 gap-3" role="radiogroup" aria-label="Answer options">
                  {LIKERT_OPTIONS.map((o) => (
                    <motion.button
                      key={o.value}
                      whileHover={{ y: -3 }}
                      whileTap={{ scale: 0.97 }}
                      onClick={() => answer(o.value)}
                      data-testid={`quiz-option-${o.short.toLowerCase()}`}
                      className={`border px-3 py-4 font-display font-bold text-xs sm:text-sm transition-colors duration-200 ${
                        answers[step] === o.value
                          ? "bg-volt text-obsidian border-volt"
                          : "border-white/15 text-white/70 hover:border-volt hover:text-white"
                      }`}
                    >
                      {o.label}
                    </motion.button>
                  ))}
                </div>
              )}

              <div className="mt-10 flex items-center gap-6">
                {step > 0 && (
                  <button
                    onClick={() => setStep((s) => s - 1)}
                    data-testid="quiz-back-button"
                    className="inline-flex items-center gap-2 text-white/50 hover:text-white text-sm font-semibold transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" /> Back
                  </button>
                )}
                {answers[step] !== null && step < TOTAL - 1 && (
                  <button
                    onClick={() => setStep((s) => s + 1)}
                    data-testid="quiz-next-button"
                    className="inline-flex items-center gap-2 text-volt text-sm font-semibold hover:text-white transition-colors"
                  >
                    Next <ArrowRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </main>
  );
}
